import {
  B2BOrder,
  OrderInfoParams,
  OrderInfoParamsSource,
} from '@buncha/B2B/hooks/type';
import {
  CancelOrderParams,
  RescheduleListData,
  RescheduleOptionNode,
  RescheduleOptionSectionNode,
  RescheduleOrderParams,
  RescheduleTimeSlots,
} from '@buncha/B2B/types/reschedule';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  decryptFromBase64,
  encryptToBase64,
  getDayLabel,
  getFormattedSlotsDate,
} from '@buncha/B2B/utils';
import {
  useFetchGetData,
  useFetchPostData,
  useFetchPutData,
} from '@buncha/config/hooks/axios';
import {useIsFocused, useRoute} from '@react-navigation/native';

import {Analytics} from '@buncha/config/analytics/analytics';
import {B2BUserScreens} from '@buncha/B2B/navigation/types';
import {CustomImage} from '@buncha/components/core/Image';
import {EventType} from '@buncha/config/analytics/type';
import Header from '@buncha/components/composites/Header/Header';
import If from '@buncha/components/conditional/If';
import LoaderComponents from '@buncha/components/composites/CustomLoader/LoaderComponents';
import {MainScreens} from '@buncha/navigation/types';
import {Modules} from '@buncha/utils/types';
import NoDataFound from '@buncha/B2B/components/noDataFound/NoDataFound';
import OrderHeader from '@buncha/B2B/components/orderHeader/OrderHeader';
import OrderRescheduleSuccess from '@buncha/B2B/components/OrderRescheduleSuccess';
import RescheduleFooter from '@buncha/B2B/components/RescheduleFooter';
import RescheduleRunDetails from '@buncha/B2B/components/RescheduleRunDetails';
import RescheduleRunSelect from '@buncha/B2B/components/RescheduleRunSelect';
import RescheduleRunTimeSelect from '@buncha/B2B/components/RescheduleRunTimeSelect';
import {Run} from '@buncha/AboutShift/types';
import {ScrollView} from 'react-native';
import {navigation} from '@buncha/config/navigation';
import {styles} from './style';
import {useAppSelector} from '@buncha/config/redux/hooks';
import {useGetLocale} from '@buncha/localization/hooks';

const Reschedule = () => {
  const [dateIndex, setDateIndex] = useState<number | undefined>();
  const [selectedRunId, setSelectedRunId] = useState<number>();
  const isFocused = useIsFocused();
  const route = useRoute();
  const params: {orderId?: string} | undefined = route?.params;
  const orderId = params?.orderId?.length
    ? decryptFromBase64(params?.orderId ?? '0')
    : '';
  const [
    getRescheduleList,
    rescheduleList,
    rescheduleListLoading,
    rescheduleListError,
  ] = useFetchGetData<{
    data: RescheduleListData;
  }>();
  const [rescheduleOrder, rescheduleOrderData, rescheduleOrderLoading] =
    useFetchPostData<{data: Run}>();
  const [cancelOrder, , cancelOrderLoading] = useFetchPutData<{
    data: B2BOrder;
  }>();
  const {body} =
    useGetLocale(Modules.B2BUserOrder, B2BUserScreens.Reschedule) ?? {};
  const timerBuffer = JSON.parse(
    useAppSelector(
      gState => gState.onboarding.constants?.AUTO_RESCHEDULE_TIMER,
    ) ?? '0',
  );

  const rescheduleListData = rescheduleList?.data;
  const onPressLoading = rescheduleOrderLoading || cancelOrderLoading;

  const legacyId = rescheduleListData?.order.legacyId || 0;
  const runId = rescheduleListData?.orderRun.id || 0;
  const deliveryWindow = `${rescheduleListData?.orderRun?.dropOffStartTime} - ${rescheduleListData?.orderRun?.dropOffEndTime}`;

  const orderDetailString = useMemo(
    () =>
      `${getDayLabel(
        rescheduleListData?.orderRun?.deliveryDate,
      )} ${deliveryWindow}`,
    [deliveryWindow, rescheduleListData?.orderRun?.deliveryDate],
  );

  const messageTime = rescheduleListData?.order?.orderMovedReq?.messageTime;
  const autoMoveTimer = useRef<number | null | undefined>(undefined);
  const timer1 = useRef<NodeJS.Timeout>();
  const orderMoved =
    (rescheduleListData?.order?.id && !messageTime) ||
    rescheduleOrderData?.data?.id
      ? true
      : false;
  const orderMovedDropOffTime = `${rescheduleOrderData?.data?.dropOffStartTime} - ${rescheduleOrderData?.data?.dropOffEndTime}`;

  const selectedRun = useMemo(() => {
    if (!dateIndex || !selectedRunId) return;
    return Object.values(
      rescheduleListData?.runs?.[dateIndex - 1] || [],
    )?.[0]?.find?.(run => {
      return run?.id === selectedRunId;
    });
  }, [dateIndex, rescheduleListData?.runs, selectedRunId]);

  const formattedDateSlots = useMemo(() => {
    return (
      rescheduleListData?.runs?.map?.((item, index) => {
        const runDate = Object.keys(item)?.[0];
        return {
          id: index + 1,
          title: getDayLabel(runDate),
          subtitle: getFormattedSlotsDate(runDate),
        } as RescheduleOptionNode;
      }) || []
    );
  }, [rescheduleListData?.runs]);

  const formattedTimeSlots = useMemo(() => {
    if (!dateIndex) return;
    const dateObj = rescheduleListData?.runs?.[dateIndex - 1];
    if (!dateObj) return;

    const runs = Object.values(dateObj)?.[0];
    if (!runs?.length) return;

    const timeSlotsPerDayPhase: RescheduleOptionSectionNode = {
      [RescheduleTimeSlots.morning]: [],
      [RescheduleTimeSlots.afternoon]: [],
      [RescheduleTimeSlots.evening]: [],
    };

    for (const run of runs) {
      const endTimeString = run?.dropOffEndTime;
      const endTime = parseFloat(endTimeString);

      if (endTimeString?.includes('AM'))
        timeSlotsPerDayPhase[RescheduleTimeSlots.morning].push({
          id: run?.id,
          title: `${run?.dropOffStartTime} - ${run?.dropOffEndTime}`,
        });
      else if (endTime === 12 || endTime === 1)
        timeSlotsPerDayPhase[RescheduleTimeSlots.morning].push({
          id: run?.id,
          title: `${run?.dropOffStartTime} - ${run?.dropOffEndTime}`,
        });
      else if (endTime <= 5)
        timeSlotsPerDayPhase[RescheduleTimeSlots.afternoon].push({
          id: run?.id,
          title: `${run?.dropOffStartTime} - ${run?.dropOffEndTime}`,
        });
      else
        timeSlotsPerDayPhase[RescheduleTimeSlots.evening].push({
          id: run?.id,
          title: `${run?.dropOffStartTime} - ${run?.dropOffEndTime}`,
        });
    }

    return timeSlotsPerDayPhase;
  }, [dateIndex, rescheduleListData?.runs]);

  const navigateToOrderInfo = useCallback(
    (source: OrderInfoParamsSource | null) => {
      navigation.replace(MainScreens.B2B, {
        screen: B2BUserScreens.OrderInfo,
        params: {
          orderId: encryptToBase64(orderId),
          source,
        } as OrderInfoParams,
      });
    },
    [orderId],
  );

  const handleOnPress = useCallback((newValue: number) => {
    setDateIndex(newValue);
    setSelectedRunId(undefined);
  }, []);

  const handleOnPressTime = useCallback((newValue: number) => {
    setSelectedRunId(newValue);
  }, []);

  const getRescheduleListFunc = useCallback(async () => {
    await getRescheduleList(
      `/api/v1/customer/timeslot/${orderId}/reschedule-list`,
      {},
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const rescheduleOrderFunc = useCallback(() => {
    rescheduleOrder('/api/v1/customer/order/reschedule-order', {
      body: {
        oldRunId: Number(rescheduleListData?.orderRun?.id),
        newRunId: Number(selectedRun?.id),
        orderIds: [Number(rescheduleListData?.order?.legacyId)],
        note: body?.rescheduleOrderReason,
      } as RescheduleOrderParams,
    });
    Analytics.eventWithProps('Reschedule', {
      type: EventType.cta,
      orderId: legacyId,
      runId,
      deliveryWindow,
      newRunId: Number(selectedRun?.id),
      newDeliveryWindow: `${selectedRun?.dropOffStartTime} ${selectedRun?.dropOffEndTime}`,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    orderId,
    rescheduleListData?.orderRun?.id,
    selectedRun?.id,
    body?.rescheduleOrderReason,
    rescheduleListData?.order?.legacyId,
    legacyId,
    runId,
    deliveryWindow,
  ]);

  const cancelOrderFunc = useCallback(async () => {
    Analytics.eventWithProps('Cancel Order', {
      type: EventType.cta,
      source: 'Reschedule',
      orderId: legacyId,
    });
    const response = await cancelOrder(`/api/v1/customer/order/${orderId}`, {
      body: {note: body?.cancellationReason} as CancelOrderParams,
    });
    if (response?.data) navigateToOrderInfo(OrderInfoParamsSource.Cancel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [body?.cancellationReason, navigateToOrderInfo, orderId, legacyId]);

  useEffect(() => {
    if (orderId && isFocused) getRescheduleListFunc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, isFocused]);

  useEffect(() => {
    if ((rescheduleListError as any)?.status === 404)
      navigateToOrderInfo(OrderInfoParamsSource.Cancel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rescheduleListError]);

  useEffect(() => {
    if (rescheduleListData?.order?.id)
      autoMoveTimer.current = messageTime
        ? new Date(messageTime).getTime() + timerBuffer * 60 * 1000 - Date.now()
        : null;

    function cleanup() {
      clearTimeout(timer1.current);
    }

    if (autoMoveTimer.current === undefined || rescheduleOrderData?.data?.id) {
      cleanup();
    } else if (autoMoveTimer.current === null || autoMoveTimer.current <= 0) {
      navigateToOrderInfo(OrderInfoParamsSource.Reschedule);
    } else {
      timer1.current = setTimeout(() => {
        navigateToOrderInfo(OrderInfoParamsSource.Reschedule);
      }, autoMoveTimer.current + 1000);

      return cleanup;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    orderId,
    messageTime,
    rescheduleOrderData?.data?.id,
    rescheduleListData?.order?.id,
  ]);

  useEffect(() => {
    if (rescheduleListData?.order?.id)
      Analytics.eventWithProps('Reschedule page', {
        type: EventType.page,
        orderId: legacyId,
        runId,
        deliveryWindow,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rescheduleListData?.order?.id]);

  return (
    <Header showDefaultLeftHeader={false} hideBottomSafeArea hideUploadFile>
      <OrderHeader
        leftIcon={
          <CustomImage
            source={require('@buncha/resource/images/vanImage.png')}
            style={styles.meijerVan}
          />
        }
        style={styles.header}
      />
      <If
        condition={rescheduleListLoading && !rescheduleListData?.runs?.length}>
        <LoaderComponents showBottomText={false} />
      </If>
      <If
        condition={!rescheduleListLoading && !rescheduleListData?.runs?.length}>
        <NoDataFound hideHeader />;
      </If>
      <If
        condition={!rescheduleListLoading && rescheduleListData?.runs?.length}>
        <If condition={orderMoved}>
          <OrderRescheduleSuccess
            orderId={orderId}
            deliveryDate={
              rescheduleOrderData?.data?.deliveryDate ||
              rescheduleListData?.orderRun?.deliveryDate ||
              ''
            }
            dropOffTimeString={orderMovedDropOffTime}
          />
        </If>
        <If condition={!orderMoved}>
          <ScrollView style={styles.scrollView}>
            <RescheduleRunDetails
              orderId={orderId}
              runDetailString={orderDetailString}
            />
            <RescheduleRunSelect
              header={body?.selectDate}
              value={dateIndex}
              data={formattedDateSlots}
              handleOnPress={handleOnPress}
              disabled={onPressLoading}
            />
            <RescheduleRunTimeSelect
              header={body?.selectTime}
              value={selectedRunId}
              data={formattedTimeSlots}
              handleOnPress={handleOnPressTime}
              expandTrigger={Boolean(dateIndex)}
              disabled={onPressLoading}
            />
            <RescheduleFooter
              rescheduleOrder={rescheduleOrderFunc}
              rescheduleOrderLoading={rescheduleOrderLoading}
              rescheduleDisabled={
                !dateIndex || !selectedRunId || onPressLoading
              }
              cancelOrder={cancelOrderFunc}
              cancelOrderLoading={cancelOrderLoading}
              cancelOrderDisabled={onPressLoading}
              runId={runId}
              legacyId={legacyId}
            />
          </ScrollView>
        </If>
      </If>
    </Header>
  );
};

export default memo(Reschedule);
